"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import type { CommunityMemberMinimal } from "@/entities/IProfile";
import type { ApiCreateGroup } from "@/generated/models/ApiCreateGroup";
import type { CreateWaveConfig, CreateWaveOutcomeType, TimeWeightedVotingSettings } from "@/types/waves.types";
import { CreateWaveGroupConfigType, CreateWaveStep } from "@/types/waves.types";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { Time } from "@/helpers/time";
import type { ApiGroupFull } from "@/generated/models/ApiGroupFull";
import { ApiWaveCreditType } from "@/generated/models/ApiWaveCreditType";
import type { Period } from "../types/period";
import type { CREATE_WAVE_VALIDATION_ERROR } from "@/helpers/waves/create-wave.validation";
import { getCreateWaveValidationErrors } from "@/helpers/waves/create-wave.validation";
import { assertUnreachable } from "@/helpers/AllowlistToolHelpers";
import { createInitialInlineGroupBuilderMap, createInitialInlineGroupBuilderState, dedupeInlineIdentities, getInlineIdentityAddresses, type CreateWaveInlineGroupBuilderState, type CreateWaveInlineGroupPanel, type CreateWaveInlineGroupRuleType } from "../groups/createWaveInlineGroupBuilder";
interface EndDateConfig {
  time: number | null;
  period: Period | null;
}
export function useWaveConfig() {
  const $ = _c(44);
  const initialType = ApiWaveType.Chat;
  const initialStep = CreateWaveStep.OVERVIEW;
  const getInitialConfig = _temp;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getInitialConfig({
      type: initialType
    });
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [config, setConfig] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      time: null,
      period: null
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [endDateConfig, setEndDateConfig] = useState(t1);
  const [step, setStep] = useState(initialStep);
  const [selectedOutcomeType, setSelectedOutcomeType] = useState(null);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [errors, setErrors] = useState(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [groupsCache, setGroupsCache] = useState(t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = createInitialInlineGroupBuilderMap();
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [groupBuilders, setGroupBuilders] = useState(t4);
  let t5;
  let t6;
  if ($[5] !== config.dates.endDate) {
    t5 = () => {
      if (config.dates.endDate === null) {
        setEndDateConfig({
          time: null,
          period: null
        });
      }
    };
    t6 = [config.dates.endDate];
    $[5] = config.dates.endDate;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  useEffect(t5, t6);
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setErrors([]);
    };
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== config) {
    t8 = [config];
    $[9] = config;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  useEffect(t7, t8);
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = overview => {
      setConfig(() => ({
        ...getInitialConfig({
          type: overview.type
        }),
        overview
      }));
      setGroupBuilders(createInitialInlineGroupBuilderMap());
    };
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const setOverview = t9;
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = dates => {
      setConfig(prev => ({
        ...prev,
        dates
      }));
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  const setDates = t10;
  let t11;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = drops => {
      setConfig(prev_0 => ({
        ...prev_0,
        drops
      }));
    };
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  const setDrops = t11;
  let t12;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = adminCanDeleteDrops => {
      setConfig(prev_1 => ({
        ...prev_1,
        drops: {
          ...prev_1.drops,
          adminCanDeleteDrops
        }
      }));
    };
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  const setDropsAdminCanDelete = t12;
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = outcomes => {
      setConfig(prev_2 => ({
        ...prev_2,
        outcomes
      }));
    };
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  const setOutcomes = t13;
  let t14;
  if ($[16] !== config || $[17] !== step) {
    t14 = t15 => {
      const {
        step: newStep,
        direction
      } = t15;
      if (direction === "forward") {
        const newErrors = getCreateWaveValidationErrors({
          config,
          step
        });
        if (newErrors.length) {
          setErrors(newErrors);
          return;
        }
      }
      setErrors([]);
      setSelectedOutcomeType(null);
      setStep(newStep);
    };
    $[16] = config;
    $[17] = step;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const onStep = t14;
  let t15;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = outcomeType => {
      setSelectedOutcomeType(outcomeType);
      setErrors([]);
    };
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  const onOutcomeTypeChange = t15;
  let t16;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = t17 => {
      const {
        group,
        groupType
      } = t17;
      if (group) {
        setGroupsCache(prev_3 => ({
          ...prev_3,
          [group.id]: group
        }));
      }
      bb34: switch (groupType) {
        case CreateWaveGroupConfigType.CAN_VIEW:
          {
            setConfig(prev_8 => ({
              ...prev_8,
              groups: {
                ...prev_8.groups,
                canView: group?.id ?? null
              }
            }));
            break bb34;
          }
        case CreateWaveGroupConfigType.CAN_DROP:
          {
            setConfig(prev_7 => ({
              ...prev_7,
              groups: {
                ...prev_7.groups,
                canDrop: group?.id ?? null
              }
            }));
            break bb34;
          }
        case CreateWaveGroupConfigType.CAN_VOTE:
          {
            setConfig(prev_6 => ({
              ...prev_6,
              groups: {
                ...prev_6.groups,
                canVote: group?.id ?? null
              }
            }));
            break bb34;
          }
        case CreateWaveGroupConfigType.CAN_CHAT:
          {
            setConfig(prev_5 => ({
              ...prev_5,
              groups: {
                ...prev_5.groups,
                canChat: group?.id ?? null
              }
            }));
            break bb34;
          }
        case CreateWaveGroupConfigType.ADMIN:
          {
            setConfig(prev_4 => ({
              ...prev_4,
              groups: {
                ...prev_4.groups,
                admin: group?.id ?? null
              }
            }));
            break bb34;
          }
        default:
          {
            assertUnreachable(groupType);
          }
      }
    };
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  const onGroupSelect = t16;
  let t17;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = (groupType_0, updater) => {
      setGroupBuilders(prev_9 => ({
        ...prev_9,
        [groupType_0]: updater(prev_9[groupType_0])
      }));
    };
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  const updateGroupBuilder = t17;
  let t18;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = (groupType_1, panel) => {
      updateGroupBuilder(groupType_1, current => ({
        ...current,
        panel
      }));
    };
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  const setGroupBuilderPanel = t18;
  let t19;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = (groupType_2, rule) => {
      updateGroupBuilder(groupType_2, current_0 => ({
        ...current_0,
        activeRule: rule,
        panel: rule === null ? current_0.panel : "rule-editor"
      }));
    };
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  const setGroupBuilderRule = t19;
  let t20;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = (groupType_3, draft) => {
      updateGroupBuilder(groupType_3, current_1 => ({
        ...current_1,
        draft
      }));
    };
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  const setGroupBuilderDraft = t20;
  let t21;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = (groupType_4, identity) => {
      updateGroupBuilder(groupType_4, current_2 => {
        const identities = dedupeInlineIdentities([...current_2.identities, identity]);
        return {
          ...current_2,
          identities,
          draft: {
            ...current_2.draft,
            group: {
              ...current_2.draft.group,
              identity_addresses: getInlineIdentityAddresses(identities)
            }
          }
        };
      });
    };
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  const addGroupBuilderIdentity = t21;
  let t22;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = (groupType_5, wallet) => {
      const normalizedWallet = wallet.trim().toLowerCase();
      updateGroupBuilder(groupType_5, current_3 => {
        const identities_0 = current_3.identities.filter(identity_0 => {
          const key = identity_0.wallet.trim().toLowerCase();
          return key !== normalizedWallet;
        });
        return {
          ...current_3,
          identities: identities_0,
          draft: {
            ...current_3.draft,
            group: {
              ...current_3.draft.group,
              identity_addresses: getInlineIdentityAddresses(identities_0)
            }
          }
        };
      });
    };
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  const removeGroupBuilderIdentity = t22;
  let t23;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = groupType_6 => {
      updateGroupBuilder(groupType_6, _temp2);
    };
    $[27] = t23;
  } else {
    t23 = $[27];
  }
  const resetGroupBuilder = t23;
  let t24;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = type_0 => {
      setConfig(prev_10 => ({
        ...prev_10,
        voting: {
          type: type_0,
          category: null,
          profileId: null,
          timeWeighted: prev_10.voting.timeWeighted
        }
      }));
    };
    $[28] = t24;
  } else {
    t24 = $[28];
  }
  const onVotingTypeChange = t24;
  let t25;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = timeWeighted => {
      setConfig(prev_11 => ({
        ...prev_11,
        voting: {
          ...prev_11.voting,
          timeWeighted
        }
      }));
    };
    $[29] = t25;
  } else {
    t25 = $[29];
  }
  const onTimeWeightedVotingChange = t25;
  let t26;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = enabled => {
      setConfig(prev_12 => ({
        ...prev_12,
        chat: {
          enabled
        }
      }));
    };
    $[30] = t26;
  } else {
    t26 = $[30];
  }
  const onChatEnabledChange = t26;
  let t27;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = category => {
      setConfig(prev_13 => ({
        ...prev_13,
        voting: {
          ...prev_13.voting,
          category
        }
      }));
    };
    $[31] = t27;
  } else {
    t27 = $[31];
  }
  const onCategoryChange = t27;
  let t28;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = profileId => {
      setConfig(prev_14 => ({
        ...prev_14,
        voting: {
          ...prev_14.voting,
          profileId
        }
      }));
    };
    $[32] = t28;
  } else {
    t28 = $[32];
  }
  const onProfileIdChange = t28;
  let t29;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = threshold => {
      setConfig(prev_15 => ({
        ...prev_15,
        approval: {
          ...prev_15.approval,
          threshold
        }
      }));
    };
    $[33] = t29;
  } else {
    t29 = $[33];
  }
  const onThresholdChange = t29;
  let t30;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = thresholdTimeMs => {
      setConfig(prev_16 => ({
        ...prev_16,
        approval: {
          ...prev_16.approval,
          thresholdTimeMs
        }
      }));
    };
    $[34] = t30;
  } else {
    t30 = $[34];
  }
  const onThresholdTimeChange = t30;
  let t31;
  if ($[35] !== config || $[36] !== endDateConfig || $[37] !== errors || $[38] !== groupBuilders || $[39] !== groupsCache || $[40] !== onStep || $[41] !== selectedOutcomeType || $[42] !== step) {
    t31 = {
      config,
      setConfig,
      endDateConfig,
      setEndDateConfig,
      step,
      selectedOutcomeType,
      errors,
      groupsCache,
      groupBuilders,
      setOverview,
      setDates,
      setDrops,
      setDropsAdminCanDelete,
      setOutcomes,
      onStep,
      onOutcomeTypeChange,
      onGroupSelect,
      setGroupBuilderPanel,
      setGroupBuilderRule,
      setGroupBuilderDraft,
      addGroupBuilderIdentity,
      removeGroupBuilderIdentity,
      resetGroupBuilder,
      onVotingTypeChange,
      onCategoryChange,
      onProfileIdChange,
      onTimeWeightedVotingChange,
      onThresholdChange,
      onThresholdTimeChange,
      onChatEnabledChange
    };
    $[35] = config;
    $[36] = endDateConfig;
    $[37] = errors;
    $[38] = groupBuilders;
    $[39] = groupsCache;
    $[40] = onStep;
    $[41] = selectedOutcomeType;
    $[42] = step;
    $[43] = t31;
  } else {
    t31 = $[43];
  }
  return t31;
}
function _temp2() {
  return createInitialInlineGroupBuilderState();
}
function _temp(t0) {
  const {
    type
  } = t0;
  const now = Time.currentMillis();
  return {
    overview: {
      type,
      name: "",
      image: null
    },
    groups: {
      canView: null,
      canDrop: null,
      canVote: null,
      canChat: null,
      admin: null
    },
    chat: {
      enabled: true
    },
    dates: {
      submissionStartDate: now,
      votingStartDate: now,
      endDate: null,
      firstDecisionTime: now,
      subsequentDecisions: [],
      isRolling: false
    },
    drops: {
      noOfApplicationsAllowedPerParticipant: null,
      requiredTypes: [],
      requiredMetadata: [],
      submissionStrategy: null,
      terms: null,
      signatureRequired: false,
      adminCanDeleteDrops: true
    },
    voting: {
      type: ApiWaveCreditType.TdhPlusXtdh,
      category: null,
      profileId: null,
      timeWeighted: {
        enabled: false,
        averagingInterval: 24,
        averagingIntervalUnit: "hours"
      }
    },
    outcomes: [],
    approval: {
      threshold: null,
      thresholdTimeMs: null
    }
  };
}